import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import pandoraAdminService from '../../../services/pandoraAdminService';
import type { PandoraDocumentDetail, PandoraParagraph } from '../../../services/pandoraAdminService';

const STATUS_STEPS = ['uploaded', 'ingested', 'classified', 'segmented', 'validated', 'promoted'];

const TAXONOMY_COLORS: Record<string, 'primary' | 'success' | 'warning'> = {
  estrutural: 'primary',
  teorico: 'success',
  factual: 'warning',
};

function getTaxonomyChipColor(domain?: string): 'primary' | 'success' | 'warning' | 'default' {
  if (!domain) return 'default';
  return TAXONOMY_COLORS[domain.toLowerCase()] ?? 'default';
}

function getStatusColor(
  status: string
): 'default' | 'info' | 'warning' | 'success' | 'error' {
  const s = status.toLowerCase();
  if (['validated', 'promoted'].includes(s)) return 'success';
  if (['rejected', 'error'].includes(s)) return 'error';
  if (s === 'segmented') return 'warning';
  return 'default';
}

export default function PandoraDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<PandoraDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [validatingParagraph, setValidatingParagraph] = useState<string | null>(null);

  const loadDocument = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await pandoraAdminService.getDocument(id);
      setDoc(data);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Erro ao carregar documento.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocument();
  }, [id]);

  const handleAction = async (
    action: string,
    fn: () => Promise<unknown>
  ) => {
    if (!id) return;
    try {
      setActionLoading(action);
      await fn();
      await loadDocument();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Erro ao executar ação.'
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleValidateParagraph = async (
    paragraphId: string,
    status: 'validated' | 'rejected'
  ) => {
    try {
      setValidatingParagraph(paragraphId);
      await pandoraAdminService.validateParagraph(paragraphId, { status });
      await loadDocument();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Erro ao validar parágrafo.'
      );
    } finally {
      setValidatingParagraph(null);
    }
  };

  if (loading || !id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !doc) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/pandora/documents')} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  if (!doc) {
    return null;
  }

  const currentStepIndex = STATUS_STEPS.indexOf(
    doc.processing_status?.toLowerCase() ?? 'uploaded'
  );
  const isPromoted = doc.processing_status?.toLowerCase() === 'promoted';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/pandora/documents')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{doc.title}</Typography>
        <Chip label={doc.processing_status} color={getStatusColor(doc.processing_status)} size="small" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={!!actionLoading || isPromoted}
          onClick={() =>
            handleAction('classify', () => pandoraAdminService.classifyDocument(id))
          }
        >
          {actionLoading === 'classify' ? <CircularProgress size={16} /> : 'Classificar'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!!actionLoading || isPromoted}
          onClick={() =>
            handleAction('segment', () => pandoraAdminService.segmentDocument(id))
          }
        >
          {actionLoading === 'segment' ? <CircularProgress size={16} /> : 'Segmentar'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!!actionLoading || isPromoted}
          onClick={() =>
            handleAction('validate', () =>
              pandoraAdminService.validateDocument(id, 'validated')
            )
          }
        >
          {actionLoading === 'validate' ? <CircularProgress size={16} /> : 'Validar Documento'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={!!actionLoading || isPromoted}
          onClick={() =>
            handleAction('reject', () =>
              pandoraAdminService.validateDocument(id, 'rejected')
            )
          }
        >
          {actionLoading === 'reject' ? <CircularProgress size={16} /> : 'Rejeitar'}
        </Button>
        {doc.processing_status?.toLowerCase() === 'validated' &&
          ['legal_case', 'law', 'verbetes'].includes(doc.category) && (
          <Button
            variant="contained"
            size="small"
            disabled={!!actionLoading}
            onClick={() =>
              handleAction('promote', () => pandoraAdminService.promoteDocument(id))
            }
          >
            {actionLoading === 'promote' ? <CircularProgress size={16} /> : 'Promover'}
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conteúdo bruto
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {doc.raw_content || '(vazio)'}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadados
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                component="pre"
                sx={{
                  maxHeight: 150,
                  overflow: 'auto',
                  fontSize: 12,
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {JSON.stringify(doc.metadata ?? {}, null, 2)}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Classificação IA
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                component="pre"
                sx={{
                  maxHeight: 150,
                  overflow: 'auto',
                  fontSize: 12,
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {JSON.stringify(doc.ai_classification ?? {}, null, 2)}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Curadoria de Paragrafos ({doc.paragraphs?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {(!doc.paragraphs || doc.paragraphs.length === 0) ? (
                <Typography color="text.secondary">
                  Nenhum paragrafo segmentado ainda.
                </Typography>
              ) : (
                doc.paragraphs.map((p: PandoraParagraph, idx: number) => (
                  <Box key={p.id} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Row 1: ORIGINAL */}
                    <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ width: 120, minWidth: 120, bgcolor: 'grey.100', p: 1.5, display: 'flex', alignItems: 'center', borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          ORIGINAL
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, p: 1.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>
                          {p.text}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Row 2: SUGESTAO IA */}
                    <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'primary.50' }}>
                      <Box sx={{ width: 120, minWidth: 120, bgcolor: 'primary.100', p: 1.5, display: 'flex', alignItems: 'center', borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight={700} color="primary.dark">
                          SUGESTAO IA
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {p.taxonomy_tag && (
                          <Chip label={p.taxonomy_tag} color={getTaxonomyChipColor(p.tag_domain)} size="small" />
                        )}
                        {p.confidence != null && (
                          <Chip
                            label={`${(p.confidence * 100).toFixed(0)}%`}
                            size="small"
                            variant="outlined"
                            color={p.confidence >= 0.8 ? 'success' : p.confidence >= 0.6 ? 'warning' : 'error'}
                          />
                        )}
                        {p.tag_domain && (
                          <Typography variant="caption" color="text.secondary">
                            {p.tag_domain} / {p.tag_class} / {p.tag_element}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {/* Row 3: CURADOR */}
                    <Box sx={{ display: 'flex', bgcolor: p.status === 'validated' ? 'success.50' : p.status === 'rejected' ? 'error.50' : 'grey.50' }}>
                      <Box sx={{ width: 120, minWidth: 120, bgcolor: p.status === 'validated' ? 'success.100' : p.status === 'rejected' ? 'error.100' : 'grey.200', p: 1.5, display: 'flex', alignItems: 'center', borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight={700} color={p.status === 'validated' ? 'success.dark' : p.status === 'rejected' ? 'error.dark' : 'text.secondary'}>
                          CURADOR
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, p: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={p.status} color={getStatusColor(p.status)} size="small" />
                        {p.curator_notes && (
                          <Typography variant="caption" color="text.secondary">{p.curator_notes}</Typography>
                        )}
                        {p.status === 'pending' && (
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                            <Button size="small" variant="contained" color="success" disabled={validatingParagraph === p.id}
                              onClick={() => handleValidateParagraph(p.id, 'validated')}>
                              {validatingParagraph === p.id ? <CircularProgress size={14} /> : 'Validar'}
                            </Button>
                            <Button size="small" variant="outlined" color="error" disabled={validatingParagraph === p.id}
                              onClick={() => handleValidateParagraph(p.id, 'rejected')}>
                              Rejeitar
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status do processamento
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {STATUS_STEPS.map((step, idx) => (
                  <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor:
                          idx <= currentStepIndex
                            ? idx === currentStepIndex
                              ? 'primary.main'
                              : 'success.main'
                            : 'grey.300',
                      }}
                    />
                    <Typography variant="body2">{step}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Categoria: {doc.category}
              </Typography>
              {doc.legal_domain && (
                <Typography variant="body2" color="text.secondary">
                  Domínio: {doc.legal_domain}
                </Typography>
              )}
              {doc.created_at && (
                <Typography variant="body2" color="text.secondary">
                  Criado: {new Date(doc.created_at).toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
